import React, { useState } from 'react';
import { Download, ExternalLink, FileText, Eye, BookOpen, RefreshCw } from 'lucide-react';

// Fonction pour formater le contenu Word de manière lisible
const formatWordContent = (content: string): string => {
  if (!content) return '';
  
  // Échapper les caractères HTML pour éviter les injections
  const escapeHtml = (text: string) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };
  
  // Diviser en paragraphes (lignes vides)
  const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim().length > 0);
  
  // Formater chaque paragraphe
  const formattedParagraphs = paragraphs.map(paragraph => {
    // Nettoyer les espaces multiples
    const cleaned = paragraph.trim().replace(/\s+/g, ' ');
    
    // Détecter les titres (lignes courtes, en majuscules, ou commençant par #)
    const isTitle = cleaned.length < 100 && (
      cleaned === cleaned.toUpperCase() ||
      cleaned.startsWith('#') ||
      /^[A-Z][^.!?]*$/.test(cleaned)
    );
    
    if (isTitle) {
      return `<h3 class="text-xl font-bold text-gray-900 mt-6 mb-3 pb-2 border-b border-gray-200">${escapeHtml(cleaned.replace(/^#+\s*/, ''))}</h3>`;
    }
    
    // Détecter les listes (lignes commençant par -, *, ou numéros)
    if (/^[-*•]\s/.test(cleaned) || /^\d+[\.\)]\s/.test(cleaned)) {
      const listItems = paragraph.split(/\n/).filter(item => item.trim().length > 0);
      const listHtml = listItems.map(item => {
        const cleanedItem = item.trim().replace(/^[-*•]\s*/, '').replace(/^\d+[\.\)]\s*/, '');
        return `<li class="mb-2 ml-4">${escapeHtml(cleanedItem)}</li>`;
      }).join('');
      return `<ul class="list-disc space-y-2 my-4">${listHtml}</ul>`;
    }
    
    // Paragraphe normal
    return `<p class="mb-4 text-gray-700 leading-relaxed">${escapeHtml(cleaned)}</p>`;
  });
  
  return formattedParagraphs.join('');
};

interface SimpleWordViewerProps {
  document: any;
  onDownload: () => void;
}

const SimpleWordViewer: React.FC<SimpleWordViewerProps> = ({ document, onDownload }) => {
  const [viewMode, setViewMode] = useState<'content' | 'download' | 'external'>('content');

  if (!document?.file_url) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Document Word non disponible</h3>
          <p className="text-gray-500">Le fichier Word n'est pas accessible pour la lecture.</p>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (viewMode) {
      case 'content':
        return (
          <div className="w-full h-full p-6 overflow-auto">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex items-center justify-between mb-6">
                  <h1 className="text-2xl font-bold text-gray-800">{document.name}</h1>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setViewMode('external')}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                    >
                      <ExternalLink className="w-4 h-4 mr-1 inline" />
                      Ouvrir dans Word
                    </button>
                    <button
                      onClick={() => setViewMode('download')}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                    >
                      <Download className="w-4 h-4 mr-1 inline" />
                      Télécharger
                    </button>
                  </div>
                </div>
                {document.content ? (
                  <div className="max-w-4xl mx-auto">
                    <div 
                      className="document-content text-gray-700 leading-relaxed"
                      style={{ fontSize: '16px', lineHeight: '1.8' }}
                      dangerouslySetInnerHTML={{
                        __html: formatWordContent(document.content)
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Contenu non disponible</h3>
                    <p className="text-gray-500 mb-6">
                      Le contenu de ce document Word n'a pas pu être extrait automatiquement.
                    </p>
                    <div className="space-y-3">
                      <button
                        onClick={() => window.open(document.file_url, '_blank')}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4 mr-2 inline" />
                        Ouvrir dans Word
                      </button>
                      <button
                        onClick={onDownload}
                        className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <Download className="w-4 h-4 mr-2 inline" />
                        Télécharger le fichier
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      
      case 'download':
        return (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center max-w-md">
              <Download className="w-16 h-16 text-blue-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Télécharger le document</h3>
              <p className="text-gray-600 mb-6">
                Téléchargez ce document Word pour l'ouvrir dans votre application préférée.
              </p>
              <div className="space-y-3">
                <button
                  onClick={onDownload}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2 inline" />
                  Télécharger le fichier
                </button>
                <button
                  onClick={() => setViewMode('content')}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Eye className="w-4 h-4 mr-2 inline" />
                  Voir le contenu
                </button>
              </div>
            </div>
          </div>
        );
      
      case 'external':
        return (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center max-w-md">
              <ExternalLink className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Ouvrir dans Word</h3>
              <p className="text-gray-600 mb-6">
                Ouvrez ce document dans Microsoft Word ou une autre application compatible.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => window.open(document.file_url, '_blank')}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4 mr-2 inline" />
                  Ouvrir dans Word
                </button>
                <button
                  onClick={() => setViewMode('content')}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Eye className="w-4 h-4 mr-2 inline" />
                  Voir le contenu
                </button>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {/* Navigation */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => setViewMode('content')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              viewMode === 'content' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Eye className="w-4 h-4 mr-2 inline" />
            Contenu
          </button>
          <button
            onClick={() => setViewMode('download')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              viewMode === 'download' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Download className="w-4 h-4 mr-2 inline" />
            Télécharger
          </button>
          <button
            onClick={() => setViewMode('external')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              viewMode === 'external' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <ExternalLink className="w-4 h-4 mr-2 inline" />
            Ouvrir dans Word
          </button>
        </div>
      </div>

      {/* Contenu */}
      <div className="flex-1 bg-gray-100">
        {renderContent()}
      </div>
    </div>
  );
};

export default SimpleWordViewer;
